<template>
  <div class="clist">
    <div class="clist-head">
      <h5 class="mb-0 font-weight-bold">Chats</h5>
      <button type="button" class="clist-new" title="New chat" @click="$emit('new')">
        <i class="fe fe-edit"></i>
      </button>
    </div>
    <div class="clist-search">
      <i class="fe fe-search"></i>
      <input v-model="q" aria-label="Search chats" placeholder="Search chats" @input="debouncedLoad" />
    </div>

    <div class="clist-items">
      <div v-if="loading && !items.length" class="text-center text-muted py-4">
        <span class="spinner-border spinner-border-sm mr-1"></span> Loading…
      </div>
      <div v-else-if="!items.length" class="text-center text-muted py-5 px-3">
        <i class="fe fe-message-circle" style="font-size: 2rem; opacity: 0.35"></i>
        <p class="mt-2 mb-3 small">{{ q ? "No chats match your search." : "No chats yet." }}</p>
        <button v-if="!q" type="button" class="btn chat-btn btn-sm px-3" @click="$emit('new')">Start a chat</button>
      </div>

      <button
        v-for="c in items"
        :key="c.id"
        type="button"
        class="citem"
        :class="{ 'citem--active': Number(c.id) === Number(activeId), 'citem--unread': c.unread_count > 0 }"
        @click="select(c)"
      >
        <ChatAvatar :name="title(c)" :id="avatarId(c)" :group="isGroup(c)" :size="44" />
        <div class="citem-main">
          <div class="d-flex align-items-baseline">
            <span class="citem-name text-truncate">{{ title(c) }}</span>
            <small class="citem-time ml-auto">{{ time(c.last_message_at) }}</small>
          </div>
          <div class="d-flex align-items-center">
            <span class="citem-preview text-truncate">
              <template v-if="c.last_message">
                <template v-if="c.last_message.sender_id === myId">You: </template>
                <template v-else-if="isGroup(c) && c.last_message.sender_name">{{ firstName(c.last_message.sender_name) }}: </template>
                <em v-if="c.last_message.deleted">Message deleted</em>
                <template v-else>{{ c.last_message.preview || "Tagged records" }}</template>
              </template>
            </span>
            <span v-if="c.unread_count > 0" class="citem-badge ml-auto">{{ c.unread_count }}</span>
          </div>
        </div>
      </button>

      <div v-if="page < lastPage" class="text-center py-2">
        <button type="button" class="btn btn-sm btn-link" :disabled="loading" @click="load(page + 1)">Load more</button>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../api";
import chat from "../store";
import ChatAvatar from "./ChatAvatar.vue";
import { POLL_UNREAD_MS, storedUser } from "../config";
import { conversationTitle, otherPeople, shortTime } from "../format";

// The user's chats, most recent first.
export default {
  name: "ConversationList",
  components: { ChatAvatar },
  props: {
    activeId: { type: [Number, String], default: null },
  },
  data() {
    return {
      q: "",
      items: [],
      page: 1,
      lastPage: 1,
      loading: false,
      timer: null,
      poll: null,
      myId: Number((storedUser() || {}).id),
    };
  },
  created() {
    this.load(1);
    chat.bus.$on("message", this.onMessage);
    chat.bus.$on("read", this.onRead);
    this.poll = setInterval(() => {
      if (!chat.state.connected && !document.hidden) this.load(1, true);
    }, POLL_UNREAD_MS);
  },
  beforeDestroy() {
    chat.bus.$off("message", this.onMessage);
    chat.bus.$off("read", this.onRead);
    clearInterval(this.poll);
    clearTimeout(this.timer);
  },
  methods: {
    time: shortTime,
    title(c) {
      return conversationTitle(c, this.myId);
    },
    isGroup(c) {
      return otherPeople(c, this.myId).length > 1;
    },
    avatarId(c) {
      const other = otherPeople(c, this.myId)[0];
      return other ? other.user_id : c.id;
    },
    firstName(name) {
      return (name || "").split(" ")[0];
    },
    select(c) {
      c.unread_count = 0; // it is marked read as soon as it opens
      this.$emit("select", c.id);
    },
    debouncedLoad() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.load(1), 300);
    },
    load(page, quiet = false) {
      if (!quiet) this.loading = true;
      return api
        .conversations({ q: this.q || undefined, page })
        .then((res) => {
          this.items = page === 1 ? res.data : this.items.concat(res.data.filter((c) => !this.items.some((i) => i.id === c.id)));
          this.page = res.current_page;
          this.lastPage = res.last_page;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    onMessage() {
      this.load(1, true);
    },
    onRead(payload) {
      const c = this.items.find((i) => Number(i.id) === Number(payload.conversation_id));
      if (c) c.unread_count = 0;
    },
    refresh() {
      return this.load(1, true);
    },
  },
};
</script>

<style scoped>
.clist {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}
.clist-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 10px;
}
.clist-new {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 0;
  background: #eef2ff;
  color: #2563eb;
  font-size: 1rem;
}
.clist-new:hover {
  background: #dbe4ff;
}
.clist-search {
  position: relative;
  margin: 0 16px 10px;
}
.clist-search i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}
.clist-search input {
  width: 100%;
  border: 0;
  background: #f1f5f9;
  border-radius: 20px;
  padding: 8px 12px 8px 34px;
  font-size: 0.88rem;
  outline: none;
}
.clist-items {
  flex: 1;
  overflow-y: auto;
}
.citem {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  border: 0;
  background: #fff;
  padding: 10px 16px;
}
.citem:hover {
  background: #f8fafc;
}
.citem--active {
  background: #eef2ff !important;
}
.citem-main {
  flex: 1;
  min-width: 0;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 8px;
}
.citem-name {
  font-weight: 600;
  color: #111827;
}
.citem-time {
  color: #94a3b8;
  padding-left: 8px;
  flex-shrink: 0;
}
.citem--unread .citem-time {
  color: #2563eb;
  font-weight: 600;
}
.citem-preview {
  color: #64748b;
  font-size: 0.85rem;
  min-width: 0;
}
.citem--unread .citem-preview {
  color: #1f2937;
  font-weight: 600;
}
.citem-badge {
  flex-shrink: 0;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  background: #2563eb;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  padding: 0 6px;
  margin-left: 8px;
}
</style>
